import { usePage as useInertiaPage } from '@inertiajs/vue3'
import type { CurrentUser, FeatureFlags, AbExperiments } from '@consumer/types/serializers'
import type Theme from '@consumer/enums/Consumer/Theme'
import { toReactive } from '@vueuse/core'

type Page<T extends Record<string, any>> = Pick<ReturnType<typeof useInertiaPage<T>>, 'props' | 'url' | 'component'>

export interface Flash {
  alert?: string
  notice?: string
}

export interface SharedProps {
  abExperiments: AbExperiments
  currentTheme?: Theme
  currentUser?: CurrentUser
  featureFlags: FeatureFlags
  referralSpec: string
  flash: Flash
  processingBank: string
  canRevealContent: boolean
}

export function usePage<T> (): Page<SharedProps & Record<string, any> & T> {
  const page = useInertiaPage<SharedProps & Record<string, any> & T>()
  if (page.component) { return page }

  // When rendering individual components via SSR, we provide the shared context.
  if (import.meta.env.SSR) { return inject('inertia', {}) as any }

  // NOTE: Used in Phlex pages, which are not a full Inertia app, so we inject
  // the global variables to allow components to inherit themes and such.
  // Prefer the injected inertia context when provided by render_ssr_component's
  // client mount script so SSR and client hydration use the same data source.
  const injectedInertia = inject<Page<SharedProps & Record<string, any> & T> | null>('inertia', null)
  if (injectedInertia) { return injectedInertia }

  return {
    props: window.InertiaProps as any,
    url: page.url || location.href,
    component: (window as any).InertiaComponent,
  }
}

export function useRecaptcha () {
  const { props } = $(usePage())

  return {
    recaptchaType: toRef(() => props.recaptchaType as 'invisible' | 'checkbox' | undefined),
    recaptchaCheckoutSiteKey: toRef(() => props.recaptchaCheckoutSiteKey as string),
    recaptchaCheckboxSiteKey: toRef(() => props.recaptchaCheckboxSiteKey as string),
    recaptchaInvisibleSiteKey: toRef(() => props.recaptchaInvisibleSiteKey as string),
  }
}

export function useSharedData<T = {}> (): SharedProps & T {
  const page = usePage()
  // Add defensive check to ensure props exists before using toReactive
  return page.props ? toReactive(toRef(page, 'props')) as any : {} as any
}
